import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Api, type ItemDto, HttpClient } from '../../generated';
import { API_BASE_URL } from '../../config/api';

// Use generated API types with additional fields
export type Item = ItemDto & {
  moq?: number;
  [key: string]: any; // Allow additional fields from API
};

// Use generated API types for search request
export type ItemSearchRequest = {
  CategoryId?: number;
  ItemType?: string;
  SearchKeyword?: string;
  Page?: number;
  PageSize?: number;
};

// Pagination response type
export interface PaginatedItemResponse {
  items: Item[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Item 360 Response types
export interface Item360Response {
  success: boolean;
  message: string;
  data: Item360Data;
  errors: string[];
  traceId: string | null;
}

export interface Item360Data {
  id: number;
  itemCode: string;
  shortCode: string;
  itemName: string;
  categoryName: string;
  moq: number;
  lastPurchasePrice: number | null;
  unitPriceUSDValue: number | null;
  lastPurchaseCurrency: string | null;
  lastPurchaseDate: string | null;
  consumptionAnalysis: {
    lastYear: {
      q1: number;
      q2: number;
      q3: number;
      q4: number;
      total: number;
    };
    thisYear: {
      q1: number;
      q2: number;
      q3: number;
      q4: number;
      total: number;
    };
    changePercentage: {
      q1: number;
      q2: number;
      q3: number;
      q4: number;
      total: number;
    };
  };
  instantInfo: {
    openRequest: number;
    onHand: number;
    openPurchaseBackOrder: number;
    openSalesBackOrder: number;
    openProductionWO: number;
  };
  relatedProducts: unknown[];
}

// Create API client instance compatible with generated API
const apiClient = new HttpClient({
  baseURL: API_BASE_URL,
  securityWorker: async () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      return {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
    }
    return {};
  },
  secure: true,
});

const api = new Api(apiClient);

interface ItemState {
  loading: boolean;
  items: Item[];
  selectedItem: Item | null;
  item360Data: Item360Data | null;
  purchaseHistory: unknown[];
  itemImages: unknown[];
  error: string | null;
  searchTerm: string;
  filters: {
    categoryId: number | null;
    itemType: string;
  };
  // Pagination state
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasMore: boolean;
  };
  operationLoading: {
    purchaseHistory: boolean;
    images: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  lastOperation: {
    type: 'create' | 'update' | 'delete' | null;
    success: boolean;
    message: string | null;
  };
}

const initialState: ItemState = {
  loading: false,
  items: [],
  selectedItem: null,
  item360Data: null,
  purchaseHistory: [],
  itemImages: [],
  error: null,
  searchTerm: '',
  filters: {
    categoryId: null,
    itemType: '',
  },
  pagination: {
    currentPage: 1,
    pageSize: 50,
    totalCount: 0,
    totalPages: 0,
    hasMore: false,
  },
  operationLoading: {
    purchaseHistory: false,
    images: false,
    create: false,
    update: false,
    delete: false,
  },
  lastOperation: {
    type: null,
    success: false,
    message: null,
  },
};

// Generated API kullanımı için örnek async thunk'lar
// Bu thunk'lar generated API ile değiştirilecek

export const getItems = createAsyncThunk(
  "item/getItems",
  async (params: ItemSearchRequest = {}, thunkAPI) => {
    try {
      // Generated API kullanımı
      const response = await api.api.itemList(params);
      
      return response;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Ürünler yüklenemedi';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// New pagination action
export const getItemsWithPagination = createAsyncThunk(
  "item/getItemsWithPagination",
  async (params: ItemSearchRequest = {}, thunkAPI) => {
    try {
      // Set default pagination parameters
      const requestParams: any = {
        Page: params.Page || 1,
        PageSize: params.PageSize || 50,
      };
      
      if (params.CategoryId !== undefined && params.CategoryId !== null) {
        requestParams.CategoryId = params.CategoryId;
      }
      
      if (params.ItemType !== undefined && params.ItemType !== null) {
        requestParams.ItemType = params.ItemType;
      }
      
      if (params.SearchKeyword !== undefined && params.SearchKeyword !== null && params.SearchKeyword.trim() !== '') {
        requestParams.SearchKeyword = params.SearchKeyword;
      }
      
      const response = await api.api.itemList(requestParams);
      
      return { response, params: requestParams };
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Ürünler yüklenemedi';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const getItemById = createAsyncThunk(
  "item/getItemById",
  async (id: number, thunkAPI) => {
    try {
      // Generated API kullanımı
      const response = await api.api.itemDetail({ id });
      
      return response;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Ürün detayları yüklenemedi';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const searchItems = createAsyncThunk(
  "item/searchItems",
  async (_searchTerm: string, thunkAPI) => {
    try {
      // Generated API kullanımı - şimdilik tüm itemları getir, client-side filtreleme yapacağız
      const response = await api.api.itemList({});
      
      return response;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Ürün arama başarısız';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const getItem360List = createAsyncThunk(
  "item/getItem360List",
  async (params: { id: number; filterId?: number }, thunkAPI) => {
    try {
      // Generated API kullanımı - ID filtresi ile
      const response = await api.api.item360List({ 
        id: params.id,
        ...(params.filterId && { filterId: params.filterId })
      });
      
      return response;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Item 360 listesi yüklenemedi';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const getItemPurchaseHistory = createAsyncThunk(
  "item/getItemPurchaseHistory",
  async (params: { itemId: number; pageIndex?: number; pageSize?: number }, thunkAPI) => {
    try {
      // Generated API kullanımı
      const response = await api.api.itemPurchaseHistoryList({
        itemId: params.itemId,
        ...(params.pageIndex !== undefined && { pageIndex: params.pageIndex }),
        ...(params.pageSize !== undefined && { pageSize: params.pageSize })
      });
      
      return { itemId: params.itemId, response };
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Satın alma geçmişi yüklenemedi';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Create Item
export const createItem = createAsyncThunk(
  "item/createItem",
  async (itemData: ItemDto, thunkAPI) => {
    try {
      const response = await api.api.itemCreate(itemData);
      
      return response;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Ürün oluşturulamadı';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Update Item
export const updateItem = createAsyncThunk(
  "item/updateItem",
  async (params: { id: number; itemData: Partial<ItemDto> }, thunkAPI) => {
    try {
      // Note: API'de update endpoint'i yok, bu yüzden create kullanıyoruz
      // Gerçek uygulamada update endpoint'i olmalı
      const response = await api.api.itemCreate(params.itemData as ItemDto);
      
      return { id: params.id, response };
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Ürün güncellenemedi';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Delete Item
export const deleteItem = createAsyncThunk(
  "item/deleteItem",
  async (id: number, thunkAPI) => {
    try {
      const response = await api.api.itemDelete({ id });
      
      return { id, response };
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Ürün silinemedi';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Item Images Management
export const getItemImages = createAsyncThunk(
  "item/getItemImages",
  async (itemId: number, thunkAPI) => {
    try {
      const response = await api.api.itemImagesList({ itemId });
      
      return { itemId, response };
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Ürün resimleri yüklenemedi';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const uploadItemImages = createAsyncThunk(
  "item/uploadItemImages",
  async (params: { itemId: number; files: File[] }, thunkAPI) => {
    try {
      const response = await api.api.itemImagesCreate(
        { itemId: params.itemId },
        { files: params.files as any },
        {}
      );
      
      return { itemId: params.itemId, response };
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Resimler yüklenemedi';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);


const itemSlice = createSlice({
  name: "item",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearItems: (state) => {
      state.items = [];
      state.error = null;
    },
    clearSelectedItem: (state) => {
      state.selectedItem = null;
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        categoryId: null,
        itemType: '',
      };
      state.searchTerm = '';
    },
    resetPagination: (state) => {
      state.pagination = {
        currentPage: 1,
        pageSize: 50,
        totalCount: 0,
        totalPages: 0,
        hasMore: false,
      };
      state.items = [];
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearPurchaseHistory: (state) => {
      state.purchaseHistory = [];
    },
    clearItemImages: (state) => {
      state.itemImages = [];
    },
    clearLastOperation: (state) => {
      state.lastOperation = {
        type: null,
        success: false,
        message: null,
      };
    },
    setOperationLoading: (state, action) => {
      const { operation, loading } = action.payload;
      if (operation in state.operationLoading) {
        state.operationLoading[operation as keyof typeof state.operationLoading] = loading;
      }
    },
  },
  extraReducers(builder) {
    // Get Items
    builder.addCase(getItems.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getItems.fulfilled, (state, action) => {
      state.loading = false;
      // Generated API response structure - data is directly the array or wrapped
      const responseData = action.payload;
      if (Array.isArray(responseData)) {
        state.items = responseData;
      } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        // If response has data property
        const data = (responseData as { data: unknown }).data;
        if (Array.isArray(data)) {
          state.items = data;
        } else if (data && typeof data === 'object' && 'items' in data) {
          state.items = (data as { items: Item[] }).items;
        }
      }
      state.error = null;
    });
    builder.addCase(getItems.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Ürünler yüklenemedi';
    });

    // Get Items With Pagination
    builder.addCase(getItemsWithPagination.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getItemsWithPagination.fulfilled, (state, action) => {
      state.loading = false;
      const { response, params } = action.payload;
      
      // Handle paginated response
      if (response && typeof response === 'object' && 'data' in response) {
        const data = (response as { data: any }).data;
        
        if (data && typeof data === 'object' && 'items' in data) {
          const paginatedData = data as PaginatedItemResponse;
          
          // If it's the first page, replace items, otherwise append
          if (params.Page === 1) {
            state.items = paginatedData.items;
          } else {
            state.items = [...state.items, ...paginatedData.items];
          }
          
          // Update pagination state
          state.pagination = {
            currentPage: paginatedData.page,
            pageSize: paginatedData.pageSize,
            totalCount: paginatedData.totalCount,
            totalPages: paginatedData.totalPages,
            hasMore: paginatedData.page < paginatedData.totalPages,
          };
        }
      }
      state.error = null;
    });
    builder.addCase(getItemsWithPagination.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Ürünler yüklenemedi';
    });

    // Get Item By ID
    builder.addCase(getItemById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getItemById.fulfilled, (state, action) => {
      state.loading = false;
      // Generated API response structure
      const responseData = action.payload;
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        state.selectedItem = (responseData as any).data;
      } else {
        state.selectedItem = responseData as Item;
      }
      state.error = null;
    });
    builder.addCase(getItemById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Ürün detayları yüklenemedi';
    });

    // Search Items
    builder.addCase(searchItems.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(searchItems.fulfilled, (state, action) => {
      state.loading = false;
      // Generated API response structure - data is directly the array or wrapped
      const responseData = action.payload;
      if (Array.isArray(responseData)) {
        state.items = responseData;
      } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        // If response has data property
        const data = (responseData as { data: unknown }).data;
        if (Array.isArray(data)) {
          state.items = data;
        } else if (data && typeof data === 'object' && 'items' in data) {
          state.items = (data as { items: Item[] }).items;
        }
      }
      state.error = null;
    });
    builder.addCase(searchItems.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Ürün arama başarısız';
    });

    // Get Item 360 List
    builder.addCase(getItem360List.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getItem360List.fulfilled, (state, action) => {
      state.loading = false;
      // API response structure: { success, message, data, errors, traceId }
      const responseData = action.payload;
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        // Response has the structure: { success, message, data, errors, traceId }
        state.item360Data = responseData.data;
      } else {
        // Fallback for direct data
        state.item360Data = responseData;
      }
      state.error = null;
    });
    builder.addCase(getItem360List.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Item 360 listesi yüklenemedi';
    });

    // Get Item Purchase History
    builder.addCase(getItemPurchaseHistory.pending, (state) => {
      state.operationLoading.purchaseHistory = true;
      state.error = null;
    });
    builder.addCase(getItemPurchaseHistory.fulfilled, (state, action) => {
      state.operationLoading.purchaseHistory = false;
      const responseData = action.payload.response;
      if (Array.isArray(responseData)) {
        state.purchaseHistory = responseData;
      } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const data = (responseData as { data: unknown }).data;
        if (Array.isArray(data)) {
          state.purchaseHistory = data;
        } else if (data && typeof data === 'object' && 'items' in data) {
          state.purchaseHistory = (data as { items: unknown[] }).items;
        }
      }
      state.error = null;
    });
    builder.addCase(getItemPurchaseHistory.rejected, (state, action) => {
      state.operationLoading.purchaseHistory = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Satın alma geçmişi yüklenemedi';
    });

    // Create Item
    builder.addCase(createItem.pending, (state) => {
      state.operationLoading.create = true;
      state.error = null;
    });
    builder.addCase(createItem.fulfilled, (state, action) => {
      state.operationLoading.create = false;
      state.lastOperation = {
        type: 'create',
        success: true,
        message: 'Ürün başarıyla oluşturuldu',
      };
      // Add new item to the list if response contains item data
      const responseData = action.payload;
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const newItem = (responseData as { data: unknown }).data;
        if (newItem) {
          state.items.unshift(newItem);
        }
      }
      state.error = null;
    });
    builder.addCase(createItem.rejected, (state, action) => {
      state.operationLoading.create = false;
      state.lastOperation = {
        type: 'create',
        success: false,
        message: 'Ürün oluşturulamadı',
      };
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Ürün oluşturulamadı';
    });

    // Update Item
    builder.addCase(updateItem.pending, (state) => {
      state.operationLoading.update = true;
      state.error = null;
    });
    builder.addCase(updateItem.fulfilled, (state, action) => {
      state.operationLoading.update = false;
      state.lastOperation = {
        type: 'update',
        success: true,
        message: 'Ürün başarıyla güncellendi',
      };
      // Update item in the list
      const { id, response } = action.payload;
      const responseData = response;
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const updatedItem = (responseData as { data: unknown }).data;
        if (updatedItem) {
          const index = state.items.findIndex(item => item.id === id);
          if (index !== -1) {
            state.items[index] = updatedItem;
          }
          if (state.selectedItem?.id === id) {
            state.selectedItem = updatedItem;
          }
        }
      }
      state.error = null;
    });
    builder.addCase(updateItem.rejected, (state, action) => {
      state.operationLoading.update = false;
      state.lastOperation = {
        type: 'update',
        success: false,
        message: 'Ürün güncellenemedi',
      };
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Ürün güncellenemedi';
    });

    // Delete Item
    builder.addCase(deleteItem.pending, (state) => {
      state.operationLoading.delete = true;
      state.error = null;
    });
    builder.addCase(deleteItem.fulfilled, (state, action) => {
      state.operationLoading.delete = false;
      state.lastOperation = {
        type: 'delete',
        success: true,
        message: 'Ürün başarıyla silindi',
      };
      // Remove item from the list
      const { id } = action.payload;
      state.items = state.items.filter(item => item.id !== id);
      if (state.selectedItem?.id === id) {
        state.selectedItem = null;
      }
      state.error = null;
    });
    builder.addCase(deleteItem.rejected, (state, action) => {
      state.operationLoading.delete = false;
      state.lastOperation = {
        type: 'delete',
        success: false,
        message: 'Ürün silinemedi',
      };
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Ürün silinemedi';
    });

    // Get Item Images
    builder.addCase(getItemImages.pending, (state) => {
      state.operationLoading.images = true;
      state.error = null;
    });
    builder.addCase(getItemImages.fulfilled, (state, action) => {
      state.operationLoading.images = false;
      const responseData = action.payload.response;
      if (Array.isArray(responseData)) {
        state.itemImages = responseData;
      } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const data = (responseData as { data: unknown }).data;
        if (Array.isArray(data)) {
          state.itemImages = data;
        } else if (data && typeof data === 'object' && 'images' in data) {
          state.itemImages = (data as { images: unknown[] }).images;
        }
      }
      state.error = null;
    });
    builder.addCase(getItemImages.rejected, (state, action) => {
      state.operationLoading.images = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Ürün resimleri yüklenemedi';
    });

    // Upload Item Images
    builder.addCase(uploadItemImages.pending, (state) => {
      state.operationLoading.images = true;
      state.error = null;
    });
    builder.addCase(uploadItemImages.fulfilled, (state, action) => {
      state.operationLoading.images = false;
      state.lastOperation = {
        type: 'create',
        success: true,
        message: 'Resimler başarıyla yüklendi',
      };
      // Refresh images list
      const responseData = action.payload.response;
      if (Array.isArray(responseData)) {
        state.itemImages = responseData;
      } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const data = (responseData as { data: unknown }).data;
        if (Array.isArray(data)) {
          state.itemImages = data;
        } else if (data && typeof data === 'object' && 'images' in data) {
          state.itemImages = (data as { images: unknown[] }).images;
        }
      }
      state.error = null;
    });
    builder.addCase(uploadItemImages.rejected, (state, action) => {
      state.operationLoading.images = false;
      state.lastOperation = {
        type: 'create',
        success: false,
        message: 'Resimler yüklenemedi',
      };
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Resimler yüklenemedi';
    });

  },
});

export const { 
  clearError, 
  clearItems, 
  clearSelectedItem, 
  setSearchTerm, 
  setFilters, 
  clearFilters,
  resetPagination,
  setPagination,
  clearPurchaseHistory,
  clearItemImages,
  clearLastOperation,
  setOperationLoading
} = itemSlice.actions;
export default itemSlice.reducer;
